/**
 * Staff-CRM shell branding - the logo in the app header (API `crm.branding`).
 *
 * Separate from the invoice logo (`crm.company`, see lib/sales.ts): that one is
 * printed on a white document, this one sits on the app chrome and therefore
 * carries its own dark-theme variant, a square mark for the collapsed sidebar
 * and a display height for each.
 *
 * Everything empty = the built-in WebAdmin logo, so a CRM that was never branded
 * keeps looking exactly as it does today.
 */
import { api } from "./api";

export interface CrmBranding {
  /** Light-theme logo (media library URL); empty = the built-in WebAdmin logo. */
  logoUrl: string;
  /** Dark-theme logo; empty = `logoUrl` is used in both themes. */
  logoDarkUrl: string;
  /** Rendered height in px. */
  logoHeight: number;
  /**
   * Light-theme square mark for the collapsed sidebar; empty = the wide logo is
   * scaled down to fit, or the built-in WebAdmin icon when there is no custom logo.
   */
  iconUrl: string;
  /** Dark-theme square mark; empty = `iconUrl` is used in both themes. */
  iconDarkUrl: string;
  /** Rendered height of the square mark in px. */
  iconHeight: number;
  /** Accessible name for the logo image; empty = "WebAdmin". */
  alt: string;
}

/** The built-in logo renders at 24px (`h-6`) in the header. */
export const CRM_LOGO_HEIGHT_DEFAULT = 24;
export const CRM_LOGO_HEIGHT_MIN = 16;
export const CRM_LOGO_HEIGHT_MAX = 64;

/** The collapsed rail is 58px wide and its icons sit in a 44px pill. */
export const CRM_ICON_HEIGHT_DEFAULT = 26;
export const CRM_ICON_HEIGHT_MIN = 16;
export const CRM_ICON_HEIGHT_MAX = 40;

export const EMPTY_BRANDING: CrmBranding = {
  logoUrl: "",
  logoDarkUrl: "",
  logoHeight: CRM_LOGO_HEIGHT_DEFAULT,
  iconUrl: "",
  iconDarkUrl: "",
  iconHeight: CRM_ICON_HEIGHT_DEFAULT,
  alt: "",
};

/** True when no wide logo is configured and the built-in one should be used. */
export function isDefaultBranding(b: CrmBranding): boolean {
  return !b.logoUrl && !b.logoDarkUrl;
}

/** True when a dedicated square mark was uploaded for the collapsed sidebar. */
export function hasCustomIcon(b: CrmBranding): boolean {
  return !!(b.iconUrl || b.iconDarkUrl);
}

/**
 * Public read - deliberately unauthenticated, because the login screen renders
 * before there is a session and should already carry the operator's brand.
 */
export const getPublicBranding = () => api.get<CrmBranding>("/public/crm-branding");

export const getBranding = () => api.get<CrmBranding>("/admin/platform/crm-branding");
export const saveBranding = (body: Partial<CrmBranding>) =>
  api.put<CrmBranding>("/admin/platform/crm-branding", body);
